import React, { useState } from "react";

const ImgContent = ({ data, imagePosition = "left" }) => {
  const { image, title, subtitle, content } = data;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Dynamic layout based on imagePosition prop
  const isImageLeft = imagePosition === "left";
  const flexDirection = isImageLeft ? "md:flex-row" : "md:flex-row-reverse";

  return (
    <div className="w-full max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8">
      <div className={`flex flex-col ${flexDirection} gap-6 lg:gap-12 items-center`}>
        
        {/* Image Section */}
        <div className="w-full md:w-1/2 relative group">
          <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 bg-gray-100">
            
            {/* Loading State */}
            {!imageLoaded && !imageError && (
              <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite] rounded-2xl">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-[#10c9c0] border-t-transparent rounded-full animate-spin"></div>
                </div>
              </div>
            )}

            {/* Error State */}
            {imageError && (
              <div className="w-full h-64 sm:h-80 md:h-96 bg-gray-200 flex flex-col items-center justify-center rounded-2xl">
                <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-500 text-sm">Failed to load image</p>
              </div>
            )}

            {/* Main Image */}
            <img
              src={image}
              className={`w-full h-64 sm:h-80 md:h-96 object-cover transition-all duration-700 hover:scale-105 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              alt="Akiya and AKIYA2.0"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              loading="lazy"
            />

            {/* Subtle Overlay for Modern Look */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
        </div>

        {/* Text Content */}
        <div className="w-full md:w-1/2 flex flex-col justify-center space-y-4">
          
          {/* Title */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">
              {title}
            </h1>
            
            {/* Subtitle */}
            <h2 className="text-[#10c9c0] text-2xl font-light leading-relaxed">
              {subtitle}
            </h2>
          </div>

          {/* Decorative Line */}
          <div className="w-16 h-1 bg-gradient-to-r from-[#10c9c0] to-[#10c9c0]/50 rounded-full"></div>
          
          {/* Content */}
          <div className="prose prose-gray max-w-none">
            <p className="text-justify mt-4 text-[#949995] text-base font-normal leading-6">
              {content}
            </p>
          </div>

          {/* Optional CTA Section */}
          {/* <div className="pt-4">
            <div className="flex items-center space-x-2 text-[#10c9c0] hover:text-[#0ea59c] transition-colors duration-200 cursor-pointer group/cta">
              <span className="text-sm font-medium">Learn more</span>
              <svg 
                className="w-4 h-4 transform group-hover/cta:translate-x-1 transition-transform duration-200" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </div> */}
        </div>
      </div>

      {/* Shimmer Animation Styles */}
      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>
  );
};

// Example usage with different layouts
const ImgContentShowcase = () => {
  const sampleData = {
    image: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=800&q=80",
    title: "Modern Living Spaces",
    subtitle: "Discover Your Perfect Home",
    content: "Experience the perfect blend of traditional Japanese architecture and modern amenities. Our carefully curated properties offer unique opportunities for both investors and homeowners looking for something truly special."
  };

  return (
    <div className="space-y-16 py-8">
      {/* Left Image Layout */}
      <ImgContent data={sampleData} imagePosition="left" />
      
      {/* Right Image Layout */}
      <ImgContent data={sampleData} imagePosition="right" />
    </div>
  );
};

export default ImgContent;